import * as amplitude from '@amplitude/analytics-browser';
import { defaultEventTrackingAdvancedPlugin } from '@amplitude/plugin-default-event-tracking-advanced-browser';

import {
  type AnalyticsDevEventType,
  publishAnalyticsDevEvent,
} from '~/common/analytics/analyticsDevEvents';
import { createAmplitudeElementAttributesPlugin } from '~/common/analytics/elementTracking';
import {
  loadSegment,
  segmentIdentify,
  segmentPage,
  segmentTrack,
} from '~/common/analytics/segmentClient';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { isDev } from '~/dev/utils/isDev';

type AnalyticsProperties = object;

type AnalyticsUser = {
  client: number;
  id: number | string;
  client_name?: string;
  departments?: string[];
  is_workflow_admin?: boolean;
  permission_names?: string[];
  role?: string;
  role_uuid?: {
    name?: string;
    role_type?: string;
  };
};

// Amplitude's defaults flush every 30 events or every second; widening both
// collapses interaction bursts into far fewer requests. Keep the interval
// modest — queued events are lost if the tab dies before the next flush.
const AMPLITUDE_FLUSH_QUEUE_SIZE = 50;
const AMPLITUDE_FLUSH_INTERVAL_MS = 10_000;

export function startAnalytics() {
  loadSegment();

  if (!isDev) {
    amplitude.init(
      getEnvVariable('REACT_APP_AMPLITUDE_API_KEY') ?? '',
      undefined,
      {
        defaultTracking: {
          pageViews: false,
        },
        flushIntervalMillis: AMPLITUDE_FLUSH_INTERVAL_MS,
        flushQueueSize: AMPLITUDE_FLUSH_QUEUE_SIZE,
      },
    );
    amplitude.add(defaultEventTrackingAdvancedPlugin());
    amplitude.add(createAmplitudeElementAttributesPlugin());
  }

  publishAnalyticsDevEvent({
    attributes: {},
    eventName: 'start',
    eventType: 'track',
    timestamp: Date.now(),
  });
}

export function identifyAnalyticsUser(user: AnalyticsUser | null | undefined) {
  if (!user) return;

  const userId = `${user.id}_${user.client}`;
  const userInfo = {
    role: user.role_uuid?.name ?? '',
    groupId: user.client,
    clientName: user.client_name,
    workflowAdmin: user.is_workflow_admin,
    roleType: user.role_uuid?.role_type ?? '',
    departments: (user.departments ?? []).join('; '),
    rolePermissions: (user.permission_names ?? []).join('; '),
  };

  segmentIdentify(userId, userInfo);
  if (!isDev) amplitude.setUserId(userId);

  publishAnalyticsDevEvent({
    attributes: userInfo,
    eventName: 'identify-user',
    eventType: 'track',
    timestamp: Date.now(),
  });
}

export function trackAnalyticsEvent(
  name: string,
  properties?: AnalyticsProperties,
  eventType: AnalyticsDevEventType = 'track',
) {
  publishAnalyticsDevEvent({
    attributes: properties ? { ...properties } : {},
    eventName: name,
    eventType,
    timestamp: Date.now(),
  });

  segmentTrack(name, properties);
  amplitude.track(name, properties);
}

export function trackAnalyticsPage(properties?: AnalyticsProperties) {
  publishAnalyticsDevEvent({
    attributes: properties ? { ...properties } : {},
    eventName: 'page_view',
    eventType: 'track',
    timestamp: Date.now(),
  });

  segmentPage(properties);
}
